import { createContext, useContext } from 'react';
import type { Diffusion } from './diffusion';
import type { EvenementMoteur } from './moteur';
import type { StockageFile } from './stockage';
import type { DemandeMiseEnFile, EnvoiContact, Recensement, ResultatMiseEnFile, ResumeEnvoi } from './types';

/**
 * File d'envoi vue de l'interface (docs/UI.md § Écran 3, mode hors ligne) :
 * un instantané sans contenu libre pour les bandeaux et les listes, et les
 * gestes de l'utilisateur. Le fournisseur vit dans `contexte.tsx`.
 */

export interface InstantaneFile {
  /** Stockage ouvert et premier recensement fait. */
  pret: boolean;
  stockage: 'indexeddb' | 'memoire';
  enLigne: boolean;
  /** Nombres seulement, tous comptes confondus. */
  recensement: Recensement | null;
  /** Saisies du compte connecté (envois puis brouillons). */
  resumes: ResumeEnvoi[];
  enAttente: number;
  refusees: number;
  envoiEnCours: { contactId: string; rang: number; total: number } | null;
  prochainEssai: number | null;
  echecsConsecutifs: number;
  /** Heure du premier échec de la série en cours. */
  enEchecDepuis: number | null;
  dernierePanne: string | null;
  dernierEnvoiLe: number | null;
  /** « Tout est envoyé » : heure de la fin de tournée, effacée après 6 s. */
  toutEnvoyeLe: number | null;
  effaceesPourAnciennete: number;
  miseAJourRequise: boolean;
  /** Cette version attend que les autres onglets ferment la base locale. */
  miseAJourBloquee: boolean;
  /** Saisies d'autres comptes, tant que l'utilisateur connecté n'a pas choisi quoi en faire. */
  autresComptes: number;
  /** Compte désactivé (`COMPTE_INACTIF`) : ses saisies viennent d'être effacées. */
  compteDesactive: boolean;
}

export interface ApiFile {
  instantane: InstantaneFile;
  stockage: StockageFile | null;
  diffusion: Diffusion;
  mettreEnFile(demande: DemandeMiseEnFile): Promise<ResultatMiseEnFile>;
  attendreContact(contactId: string, delaiMs: number): Promise<'envoye' | 'refuse' | 'en_attente'>;
  lire(contactId: string): Promise<EnvoiContact | undefined>;
  envoyerMaintenant(): void;
  envoyerEvaluationSeule(contactId: string): Promise<void>;
  abandonner(contactId: string): Promise<void>;
  ecouter(ecouteur: (evenement: EvenementMoteur) => void): () => void;
  /** « Effacer ces saisies et continuer » (saisies d'un autre compte). */
  effacerAutresComptes(): Promise<void>;
  oublierPurgeAnciennete(): void;
  oublierCompteDesactive(): void;
}

export interface DialogueDeconnexion {
  nombre: number;
  refusees: number;
  enLigne: boolean;
  apresEnvoiPartiel: boolean;
}

export interface ApiDeconnexion {
  dialogue: DialogueDeconnexion | null;
  /** Sans saisie du compte, déconnecte ; sinon ouvre la confirmation. */
  demander(): Promise<void>;
  /** « Me déconnecter et effacer ». */
  confirmer(): Promise<void>;
  /** « Envoyer, puis me déconnecter ». */
  envoyerPuisDeconnecter(): Promise<void>;
  annuler(): void;
}

export interface ValeurFile {
  file: ApiFile;
  deconnexion: ApiDeconnexion;
}

export const ContexteFile = createContext<ValeurFile | null>(null);

export function useFile(): ApiFile {
  const valeur = useContext(ContexteFile);
  if (!valeur) throw new Error('useFile hors du fournisseur');
  return valeur.file;
}

export function useDeconnexion(): ApiDeconnexion {
  const valeur = useContext(ContexteFile);
  if (!valeur) throw new Error('useDeconnexion hors du fournisseur');
  return valeur.deconnexion;
}
